import { Delete, Edit, Person } from "@mui/icons-material";
import {
  Box,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  Paper,
  Switch,
  Typography
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import axios from "axios";
import { TCustomer, TUser } from "../types/AllTypes";
import { toIsoString } from "../utils";
import { MyButton } from "./MyButton";

export const Profile = ({
  user = undefined,
  customer = undefined,
  logoutCustomer,
  allowLeave = true
}: {
  user?: TUser;
  customer?: TCustomer;
  logoutCustomer: () => void;
  allowLeave?: boolean;
}) => {
  if (!user && !customer) {
    return (
      <Box
        padding={2}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        <Typography variant="h6" color="text.secondary">
          Nie ste prihlásený.
        </Typography>
      </Box>
    );
  }
  return (
    <>
      <Box
        padding={2}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        <Paper
          elevation={4}
          sx={{
            padding: 4,
            borderRadius: 6,
            minWidth: 340,
            maxWidth: 420,
            boxShadow: 6
          }}
        >
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={1.5}
          >
            {user && user.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt="User Avatar"
                style={{
                  width: 60,
                  height: 60,
                  borderRadius: "50%",
                  objectFit: "cover",
                  marginBottom: "8px"
                }}
              />
            ) : (
              <Person sx={{ fontSize: 60, color: "primary.main", mb: 1 }} />
            )}
            <Typography
              variant="h4"
              sx={{
                textAlign: "center",
                fontWeight: 800,
                letterSpacing: 1,
                mb: 1,
                color: "primary.main"
              }}
            >
              Vitaj, {user ? user.first_name : customer?.meno}!
            </Typography>
            <Box width="100%" mt={1}>
              <Typography
                variant="h6"
                sx={{
                  textAlign: "center",
                  fontWeight: 600,
                  color: "text.secondary",
                  mb: 0.5
                }}
              >
                {user ? user.first_name + " " + user.last_name : customer?.meno}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  textAlign: "center",
                  color: "text.secondary",
                  mb: 0.5,
                  fontSize: 16
                }}
              >
                {user ? user.phone : customer?.cislo || "N/A"}
              </Typography>
              {user && user.email && (
                <Typography
                  variant="body1"
                  sx={{
                    textAlign: "center",
                    color: "text.secondary",
                    mb: 1,
                    fontSize: 16
                  }}
                >
                  {user.email}
                </Typography>
              )}
              {user && user.isAdmin && (
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: "center",
                    color: "primary.dark",
                    fontWeight: 700,
                    letterSpacing: 1,
                    mt: 1
                  }}
                >
                  Admin
                </Typography>
              )}
              {customer && (
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: "center",
                    color: "primary.dark",
                    fontWeight: 700,
                    letterSpacing: 1,
                    mt: 1
                  }}
                >
                  Host
                </Typography>
              )}
            </Box>
          </Box>
        </Paper>
      </Box>
      <Box position={"fixed"} bottom={20} width={"100%"} paddingX={2}>
        <MyButton
          fullWidth
          color="error"
          onClick={
            user
              ? () => {
                  if (allowLeave) {
                    axios
                      .delete("/logout")
                      .then(() => {
                        window.location.reload();
                      })
                      .catch(() => {
                        window.location.reload();
                      });
                  } else {
                    alert("Ste offline, nemozete sa odhlasit");
                  }
                }
              : logoutCustomer
          }
          type="submit"
        >
          <Typography
            sx={{
              fontWeight: 700
            }}
            variant="h6"
          >
            Odhlasit sa
          </Typography>
        </MyButton>
      </Box>
    </>
  );
};

export const User = ({
  user,
  onEditClick = undefined
}: {
  user: TUser;
  onEditClick?: (user: TUser) => void;
}) => {
  return (
    <Box
      padding={3}
      paddingY={1.5}
      minWidth={"350px"}
      sx={{ margin: "0 auto" }}
    >
      <Paper elevation={1}>
        <Box
          padding={2}
          border={user.isOCAdmin || user.isAdmin ? 1 : 0}
          borderColor={user.isAdmin ? "primary.dark" : "primary.light"}
          borderRadius={2}
        >
          <Grid container>
            <Grid item xs={3} display="flex" justifyContent="center">
              {user.avatarUrl ? (
                <img
                  src={user.avatarUrl}
                  alt="User Avatar"
                  style={{
                    width: 60,
                    height: 60,
                    borderRadius: "50%",
                    objectFit: "cover"
                  }}
                />
              ) : (
                <Person sx={{ fontSize: 60, color: "primary.main" }} />
              )}
            </Grid>
            <Grid item xs={9}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {user.first_name} {user.last_name}
              </Typography>
              <Typography
                variant="body1"
                display={"flex"}
                justifyContent={"space-between"}
              >
                {user.phone || user.email
                  ? (user.phone || user.email).toString().length > 22
                    ? (user.phone || user.email).toString().slice(0, 19) + "..."
                    : (user.phone || user.email).toString()
                  : "N/A"}
                {onEditClick && (
                  <IconButton
                    onClick={() => {
                      onEditClick(user);
                    }}
                    aria-label="Edit User"
                    color="primary"
                    style={{ marginLeft: "10px" }}
                  >
                    <Edit />
                  </IconButton>
                )}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export const UserInfo = ({ user }: { user: TUser }) => {
  return (
    <Box
      padding={2}
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
    >
      <Paper
        elevation={4}
        sx={{
          padding: 4,
          borderRadius: 6,
          minWidth: 340,
          boxShadow: 6
        }}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={1.5}
        >
          {user && user.avatarUrl ? (
            <img
              src={user.avatarUrl}
              alt="User Avatar"
              style={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                objectFit: "cover",
                marginBottom: "8px"
              }}
            />
          ) : (
            <Person sx={{ fontSize: 60, color: "primary.main", mb: 1 }} />
          )}
          <Typography
            variant="h4"
            sx={{
              textAlign: "center",
              fontWeight: 800,
              letterSpacing: 1,
              mb: 1,
              color: "primary.main"
            }}
          >
            {user.first_name} {user.last_name}
          </Typography>
          <Box width="100%" mt={1}>
            <Typography
              variant="h6"
              sx={{
                textAlign: "center",
                fontWeight: 600,
                color: "text.secondary",
                mb: 0.5
              }}
              onClick={() => {
                if (user.phone) {
                  navigator.clipboard.writeText(user.phone);
                  alert("Phone copied to clipboard!");
                }
              }}
              title="Klikni pre kopirovanie"
            >
              {user.phone || "N/A"}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                textAlign: "center",
                fontWeight: 600,
                color: "text.secondary",
                mb: 0.5
              }}
              onClick={() => {
                if (user.email) {
                  navigator.clipboard.writeText(user.email);
                  alert("Email copied to clipboard!");
                }
              }}
              title="Klikni pre kopirovanie"
            >
              {user.email || "N/A"}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                textAlign: "center",
                fontWeight: 600,
                color: "text.secondary",
                mb: 0.5
              }}
            >
              ID: {user.id}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                textAlign: "center",
                fontWeight: 600,
                color: "text.secondary",
                mb: 0.5
              }}
              onClick={() => {
                navigator.clipboard.writeText(user.uid);
                alert("Opencamp ID copied to clipboard!");
              }}
              title="Klikni pre kopirovanie"
            >
              OC ID: {user.uid}
            </Typography>
            {user.isOCAdmin && (
              <Typography
                variant="body2"
                sx={{
                  textAlign: "center",
                  color: "primary.light",
                  fontWeight: 700,
                  letterSpacing: 1,
                  mt: 1
                }}
              >
                Opencamp Admin
              </Typography>
            )}
            {user.isAdmin && (
              <Typography
                variant="body2"
                sx={{
                  textAlign: "center",
                  color: "primary.dark",
                  fontWeight: 700,
                  letterSpacing: 1,
                  mt: 1
                }}
              >
                Admin
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export const UserEdit = ({
  user,
  onAdminToggle,
  onWantPizzaCancel,
  disabled = false,
  restrictDisabled = true,
  onRestrictUntilChange
}: {
  user: TUser;
  onAdminToggle: () => void;
  onWantPizzaCancel: () => void;
  disabled?: boolean;
  restrictDisabled?: boolean;
  onRestrictUntilChange: (date: Date | null) => void;
}) => {
  return (
    <Box
      padding={2}
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
    >
      <Paper
        elevation={4}
        sx={{
          padding: 2,
          borderRadius: 6,
          maxWidth: 520,
          width: "100%",
          boxShadow: 6
        }}
      >
        <FormGroup>
          <FormControlLabel
            sx={{
              margin: 1,
              marginRight: 0,
              justifyContent: "space-between"
            }}
            control={
              <Switch
                checked={user.isAdmin}
                onChange={onAdminToggle}
                disabled={disabled}
              />
            }
            label="Admin"
            labelPlacement="start"
          />
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <DateTimePicker
              sx={{ flex: 1 }}
              label="Restricted until"
              value={
                user.restrictedUntil
                  ? dayjs(toIsoString(user.restrictedUntil))
                  : null
              }
              onChange={e =>
                onRestrictUntilChange(e?.toDate() ?? user.restrictedUntil)
              }
              minutesStep={1}
              ampm={false}
              disabled={restrictDisabled}
            />
            <IconButton
              onClick={() => {
                onRestrictUntilChange(null);
              }}
              disabled={restrictDisabled || !user.restrictedUntil}
              color="error"
              sx={{ marginLeft: 1 }}
            >
              <Delete />
            </IconButton>
          </Box>
          <FormControlLabel
            sx={{
              margin: 1,
              marginRight: 0,
              justifyContent: "space-between"
            }}
            control={
              <IconButton
                onClick={onWantPizzaCancel}
                disabled={!user.wantPizzaFrom}
                color="error"
              >
                <Delete />
              </IconButton>
            }
            label={"Want pizza from " + (user.wantPizzaFrom || "N/A")}
            labelPlacement="start"
          />
        </FormGroup>
      </Paper>
    </Box>
  );
};
